import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { STATUS_URL } from '../../config/features';
import { ORGANIZATION } from '../../config/seo';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/Button';
import { Logo } from '../ui/Logo';

const NAV_LINKS = [
  { label: 'Features', to: '/#features' },
  { label: 'Pricing', to: '/#pricing' },
  { label: 'API', to: '/#api' },
];

/**
 * Chrome for the public surface (landing + legal pages): top nav with anchor
 * links and auth CTAs, and a slim footer. Never fires authenticated requests.
 */
export function PublicLayout({ children, className }: { children: ReactNode; className?: string }) {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const year = new Date().getFullYear();

  return (
    <div className="flex min-h-screen flex-col bg-bg text-text">
      <header className="relative z-10">
        <nav className="mx-auto flex h-20 max-w-[1240px] items-center justify-between gap-4 px-5 sm:px-8" aria-label="Primary">
          <Link to="/" aria-label="CMSH.io home" className="shrink-0">
            <Logo size="md" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <ul className="mr-2 hidden items-center gap-1 md:flex">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="rounded-control px-3 py-2 text-[15px] text-text-2! transition-colors hover:bg-surface-3 hover:text-text!"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
            {isAuthenticated ? (
              <Button variant="primary" size="sm" className="sm:h-11 sm:px-5 sm:text-sm" onClick={() => navigate('/app')}>
                Open dashboard
              </Button>
            ) : (
              <>
                <Button variant="secondary" size="sm" className="sm:h-11 sm:px-5 sm:text-sm" onClick={() => navigate('/login')}>
                  Sign in
                </Button>
                <Button variant="primary" size="sm" className="sm:h-11 sm:px-5 sm:text-sm" onClick={() => navigate('/register')}>
                  Get started
                </Button>
              </>
            )}
          </div>
        </nav>
      </header>

      <main className={cn('flex-1', className)}>{children}</main>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-[1240px] flex-col gap-6 px-5 py-8 sm:px-8 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col gap-3">
            <Link to="/" aria-label="CMSH.io home" className="inline-flex w-fit">
              <Logo size="sm" />
            </Link>
            <p className="text-sm text-muted">
              CMSH.io © {year} ·{' '}
              <a href={ORGANIZATION.url} target="_blank" rel="noreferrer" className="text-muted! transition-colors hover:text-text!">
                {ORGANIZATION.name}
              </a>
              . All rights reserved.
            </p>
          </div>
          <ul className="flex items-center gap-6 text-sm text-muted">
            <li>
              <Link to="/privacy-policy" className="text-muted! transition-colors hover:text-text!">
                Privacy
              </Link>
            </li>
            <li>
              <Link to="/terms-of-use" className="text-muted! transition-colors hover:text-text!">
                Terms
              </Link>
            </li>
            <li>
              <a href={STATUS_URL} target="_blank" rel="noreferrer" className="text-muted! transition-colors hover:text-text!">
                Status
              </a>
            </li>
          </ul>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legal document layout (Privacy Policy / Terms of Use)
// ---------------------------------------------------------------------------

export interface LegalSection {
  id: string;
  heading: string;
  body: ReactNode;
}

export interface LegalDocumentProps {
  title: string;
  lastUpdated: string;
  intro: ReactNode;
  sections: LegalSection[];
  /** Rendered after the sections, inside the article column. */
  footer?: ReactNode;
  /** Cross-link shown under the table of contents. */
  related?: { label: string; to: string };
}

/**
 * Readable 720px article column with a sticky table of contents on desktop.
 * The ToC is derived from the same section list that renders the h2 headings.
 */
export function LegalDocument({ title, lastUpdated, intro, sections, footer, related }: LegalDocumentProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const headings = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => el !== null);
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 },
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [sections]);

  return (
    <div className="mx-auto max-w-[1240px] px-5 pb-20 pt-6 sm:px-8 sm:pt-10">
      <div className="mb-10 max-w-[720px]">
        <h1 className="text-[34px] font-bold leading-tight tracking-tight text-text sm:text-[40px]">{title}</h1>
        <p className="mt-2 text-sm text-muted">Last updated: {lastUpdated}</p>
      </div>

      <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-16">
        <aside className="hidden w-[260px] shrink-0 lg:sticky lg:top-8 lg:block" aria-label="Table of contents">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Contents</p>
          <ol className="flex flex-col gap-0.5 border-l border-border">
            {sections.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  aria-current={activeId === s.id ? 'location' : undefined}
                  className={cn(
                    '-ml-px block border-l py-1.5 pl-4 text-[13px] leading-snug transition-colors',
                    // `!` variants: antd's unlayered `a` reset otherwise beats layered utilities on anchors.
                    activeId === s.id
                      ? 'border-link font-medium text-text!'
                      : 'border-transparent text-muted! hover:border-border-strong hover:text-text!',
                  )}
                >
                  {s.heading}
                </a>
              </li>
            ))}
          </ol>
          {related && (
            <Link to={related.to} className="mt-5 inline-block text-[13px] font-medium text-link! hover:text-link-hover!">
              {related.label} →
            </Link>
          )}
        </aside>

        <article className="w-full max-w-[720px] text-[15px] leading-7 text-text-2">
          <p className="mb-10 text-base leading-relaxed text-text-2">{intro}</p>
          {sections.map((s) => (
            <section key={s.id} aria-labelledby={s.id} className="mb-10 scroll-mt-24">
              <h2 id={s.id} className="mb-3 text-xl font-semibold leading-snug text-text">
                {s.heading}
              </h2>
              <div className="flex flex-col gap-3 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-2 [&_ul]:pl-5 [&_ul>li]:list-disc [&_ul>li]:marker:text-link [&_strong]:font-semibold [&_strong]:text-text">
                {s.body}
              </div>
            </section>
          ))}
          {footer}
        </article>
      </div>
    </div>
  );
}
